// Catalogue 1.0 maps (telcofy-demo#103, #105, city_os#83): which saved areas were made on them, and the area types
// they serve. The data API serves them (/maps, /maps/by-ids, /maps/list, /data-agg on the catalogue tables). An area made
// on them is saved with the catalogue's country code ('no', 'lt', 'lv', 'ee'). Every older save carries a 3-letter
// registry code ('NOR'), so the code alone tells a catalogue area from a legacy one: the users API keeps no other field for it.
// May import countries.ts only. No React, router, Firebase or services.
import { COUNTRIES, COUNTRY_CODES, ROAD_LINK_TYPES, areaCountry } from './countries';
import type { CountryAlias, CountryCode } from './countries';

/**
 * The catalogue's road links (maps.<country>_network_links), as an area type. Their ids are the link ids the flows
 * are keyed on, so a road-link area and the Flow lookups name the same roads. The road picker offers them in place
 * of the old OSM links.
 */
export const NETWORK_LINKS = 'network_links';

/** The catalogue's country codes: the registry aliases in lower case. */
export type CatalogueCountryCode = Lowercase<CountryAlias>;

/** The country code a saved area is stored with: the registry's for the old maps, the catalogue's for its maps. */
export type SavedCountryCode = CountryCode | CatalogueCountryCode;

/** The catalogue's code for a registry country: its alias in lower case ('NOR' -> 'no'). */
export function catalogueCountry(c: CountryCode): CatalogueCountryCode {
  return COUNTRIES[c].alias.toLowerCase() as CatalogueCountryCode;
}

/** The registry country of a catalogue code ('no' -> 'NOR'), or null for a code the registry does not know. */
export function fromCatalogueCountry(cc: string): CountryCode | null {
  return COUNTRY_CODES.find((c) => catalogueCountry(c) === cc) ?? null;
}

/** Area types only the old maps have: an area of one of these is legacy whatever its country code says. */
const OLD_MAP_TYPES: readonly string[] = ['admin_level_1', 'admin_level_2', 'admin_level_4', 'grid_250m', 'grid_1000m', 'adaptive_grid', 'settlement', 'county'];

/**
 * True for an area saved on the catalogue maps: its country code is the catalogue's own (two lower-case letters)
 * and its type is not one only the old maps have. Some older saves carry a lower-case alias too ('ee'); their
 * type gives them away.
 */
export function isCatalogueArea(area: { country_code?: string | null; type?: string | null }): boolean {
  if (typeof area.type === 'string' && OLD_MAP_TYPES.includes(area.type)) return false;
  return typeof area.country_code === 'string' && /^[a-z]{2}$/.test(area.country_code) && fromCatalogueCountry(area.country_code) !== null;
}

/**
 * True for a saved area made on the old maps, which catalogue 1.0 data does not read: a selection of map ids
 * (admin areas, grid cells) without the catalogue's country code. Custom polygons and road links are neither.
 */
export function isLegacyMapArea(area: { type?: string | null; ids?: unknown; geometry?: unknown; country_code?: string | null }): boolean {
  if (!Array.isArray(area.ids) || area.ids.length === 0 || area.geometry) return false;
  if (area.type === 'custom_polygon' || area.type === 'statens_vegvesen' || ROAD_LINK_TYPES.includes(String(area.type))) return false;
  return !isCatalogueArea(area) && areaCountry(area) !== null;
}

/**
 * The model grid is one grid, a pyramid (telcofy-demo#105): 10 km and 5 km squares, and under them 1 km squares
 * that stay whole or split into 500 m squares, which may split into 250 m squares. Every size is a rung of the same
 * grid, so the picker offers it as one layer whose rung follows the zoom, saved under this type. An area may hold
 * cells of several sizes, never a cell together with its parent or child (that would count the same ground twice).
 */
export const MODEL_GRID = 'model_grid';

/** The largest cell of the adaptive part: when zoomed in, the cells at or below it are drawn finest first. */
export const MODEL_GRID_MAX_M = 1000;

/** The cell size of a grid geo type ('grid_500m' -> 500), or null for anything else. */
export function gridSizeM(geoType: string): number | null {
  const m = /^grid_(\d+)m$/.exec(geoType);
  return m ? Number(m[1]) : null;
}

/**
 * A model-grid cell's id in a saved area: its geo type and square id ('grid_500m:22610006649000'). A square id
 * alone is not enough: a 1 km square and its south-west 500 m and 250 m children carry the same number.
 */
export function gridCellKey(geoType: string, geoId: string): string {
  return `${geoType}:${geoId}`;
}

/** The geo type and square id of a model-grid cell key, or null for anything else. */
export function parseGridCellKey(key: string): { geoType: string; geoId: string } | null {
  const m = /^(grid_\d+m):(.+)$/.exec(key);
  return m ? { geoType: m[1], geoId: m[2] } : null;
}

/**
 * The id of the square of `toM` that holds a square of `fromM`, worked out from the id alone: grid ids are fixed
 * square ids at every size. SSB (Norway): 2e13 + east * 1e7 + north of the south-west corner, in metres.
 * INSPIRE (Baltics): '<size>N<north>E<east>' in units of the size. Null for an id of neither shape.
 */
export function gridParentId(id: string, fromM: number, toM: number): string | null {
  if (/^\d{14}$/.test(id)) {
    const n = Number(id) - 2e13;
    const east = Math.floor(n / 1e7);
    const north = n - east * 1e7;
    const snap = (v: number) => Math.floor(v / toM) * toM;
    return String(2e13 + snap(east) * 1e7 + snap(north));
  }
  const unit = (m: number) => (m >= 1000 ? `${m / 1000}km` : `${m}m`);
  const m = /^(\d+(?:km|m))N(\d+)E(\d+)$/.exec(id);
  if (!m || m[1] !== unit(fromM)) return null;
  const f = toM / fromM;
  return `${unit(toM)}N${Math.floor(Number(m[2]) / f)}E${Math.floor(Number(m[3]) / f)}`;
}

/** The zooms at which the model grid steps to a finer rung: 10 km below the first, 5 km below the second. */
const RUNG_ZOOMS = [9, 11];

/** Which rung band a zoom falls in (0 = coarsest): the picker reloads the grid when this changes. */
export function modelGridBand(zoom: number): number {
  return RUNG_ZOOMS.filter((z) => zoom >= z).length;
}

/** The grid rung the picker draws at a zoom: the coarse squares when zoomed out, the adaptive cells when zoomed in. */
export function modelGridRung(zoom: number, sizes: readonly string[]): { geoTypes: string[]; adaptive: boolean } {
  const coarse = sizes.filter((g) => (gridSizeM(g) ?? 0) > MODEL_GRID_MAX_M).sort((a, b) => (gridSizeM(b) ?? 0) - (gridSizeM(a) ?? 0));
  const fine = sizes.filter((g) => (gridSizeM(g) ?? Infinity) <= MODEL_GRID_MAX_M);
  // Largest first: 10 km below zoom 9, 5 km below 11.
  for (let i = 0; i < coarse.length; i++) if (zoom < (RUNG_ZOOMS[i] ?? RUNG_ZOOMS[RUNG_ZOOMS.length - 1])) return { geoTypes: [coarse[i]], adaptive: false };
  return { geoTypes: fine.length ? fine : coarse.slice(-1), adaptive: fine.length > 0 };
}

/** Geo types the catalogue lists that the area picker leaves out: the whole country, and border stations (points). */
export const PICKER_SKIPPED_GEO_TYPES: readonly string[] = ['country', 'station'];

/** Admin levels by depth, then the older Baltic municipalities, then grids from fine to coarse. */
export function catalogueGeoTypeOrder(geoType: string): number {
  const admin = /^admin_lvl_(\d+)$/.exec(geoType);
  if (admin) return Number(admin[1]);
  if (geoType === 'lau') return 50;
  if (geoType === MODEL_GRID) return 99;
  const grid = /^grid_(\d+)m$/.exec(geoType);
  if (grid) return 100 + Number(grid[1]) / 100;
  return 1000;
}

/** The zoom above which the picker loads a catalogue layer: finer layers wait for a closer view. */
export function catalogueZoomThreshold(geoType: string): number {
  const admin = /^admin_lvl_(\d+)$/.exec(geoType);
  if (admin) return [5, 6, 8, 10, 11][Number(admin[1])] ?? 11;
  if (geoType === 'lau') return 7;
  // /maps returns every cell size of the model grid in the box, so the grid waits for a view small enough to carry
  // them all: from there the picker draws the adaptive cells. (A size filter on /maps would bring the 5 km and
  // 10 km rungs back to the zoomed-out views.)
  if (geoType === MODEL_GRID) return 11;
  const grid = /^grid_(\d+)m$/.exec(geoType);
  if (grid) {
    const m = Number(grid[1]);
    return m <= 250 ? 13 : m <= 500 ? 12.3 : m <= 1000 ? 11.6 : m <= 5000 ? 9.5 : 8;
  }
  return 13;
}
